import { WC_LIMITS, type WcFeedback, type WcFragment } from '../protocol/WebCodecsTypes';
import { WebCodecsWire } from '../protocol/WebCodecsWire';
import { WcStats, type WcWorkerStats } from '../client/WebCodecsClientTypes';

const ACTIVE_AFTER_ARRIVAL_US = 1000000;
// A NACK may run ahead of the cadence once per spacing; the regular report carries later losses.
const NACK_SPACING_US = 40000;
// Two arrivals or fewer since the last report: the slow cadence (the host reads receiver pressure
// from a report at most 40 ms old, so this is the ceiling).
const SPARSE_CADENCE_US = 40000;
export type WcReceiveSnapshot = Pick<WcFeedback, 'reassemblyBytes' | 'decodeQueueSize' | 'decoderInFlight' |
  'renderPending' | 'oldestPendingAgeUs' | 'visible' | 'needsKeyframe' | 'missingFragments'>;
/** Bounded packet history and absolute AU credits. Retirement is separate from packet arrival. */
export class WebCodecsFeedback {
  static readonly mediaWindowBytes = WC_LIMITS.windowBytes - 1024 * 1024;
  private readonly packets = new Uint32Array(8192);
  private arrivals: Array<{ packetId: number; arrivalUs: number }> = []; // not yet reported; bounded to 16
  private readonly reserved = new Map<number, number>();
  private readonly finished = new Set<number>();
  private readonly decodeTimes: number[] = [];
  private readonly renderTimes: number[] = [];
  private readonly ingressTimes: number[] = [];
  private receivedBytes = 0;
  private highest = 0;
  private retired = 0;
  private decoded = 0;
  private rendered = 0;
  private framesDecoded = 0;
  private framesPresented = 0;
  private framesDropped = 0;
  private sequence = 0;
  private lastReportUs: number | null = null;
  private lastLossReportUs: number | null = null;
  private arrivalsSinceReport = 0;
  private lastArrivalUs = -Infinity;
  private lastVisible = true;
  private lastMissingKey = '';
  private gapsSeen = 0;
  private gapsReported = 0;
  constructor(private readonly session: string, private readonly epoch: number) {}
  /** Returns false for a duplicate or a packet outside the bounded retained history. */
  arrival(fragment: WcFragment, wireBytes: number, arrivalUs: number, workerUs: number): boolean {
    const slot = fragment.packetId % this.packets.length;
    if (fragment.epoch !== this.epoch || fragment.packetId <= this.highest - 8192 || this.packets[slot] === fragment.packetId) return false;
    // A jump past the next expected id is a gap the host can only learn of from the acks: report it at once.
    if (this.highest > 0 && fragment.packetId > this.highest + 1) ++this.gapsSeen;
    this.highest = Math.max(this.highest, fragment.packetId); this.packets[slot] = fragment.packetId;
    this.receivedBytes += wireBytes; this.lastArrivalUs = Math.max(this.lastArrivalUs, workerUs); ++this.arrivalsSinceReport;
    this.arrivals.push({ packetId: fragment.packetId, arrivalUs }); if (this.arrivals.length > 16) this.arrivals.shift();
    this.sample(this.ingressTimes, Math.max(0, workerUs - arrivalUs)); return true;
  }
  reserve(frameId: number, bytes: number): boolean {
    if (frameId <= this.retired) return false;
    if (this.reserved.has(frameId)) return this.reserved.get(frameId) === bytes;
    // One MiB remains reserved for main-to-worker ingress, even when the worker is busy.
    if (bytes > WebCodecsFeedback.mediaWindowBytes - this.getOutstandingBytes()) return false;
    this.reserved.set(frameId, bytes); return true;
  }
  decodedFrame(frameId: number, elapsedUs: number): void {
    this.decoded = Math.max(this.decoded, frameId); ++this.framesDecoded; this.sample(this.decodeTimes, elapsedUs);
  }
  presentedFrame(frameId: number, intervalUs: number | null): void {
    this.rendered = Math.max(this.rendered, frameId); ++this.framesPresented;
    if (intervalUs !== null) this.sample(this.renderTimes, intervalUs);
    this.retire(frameId);
  }
  droppedFrame(frameId: number): void { if (!this.finished.has(frameId) && frameId > this.retired) ++this.framesDropped; this.retire(frameId); }
  /** Caller has reset decoder/render state, so ALL IDs through this point are intentionally retired. */
  retireThrough(frameId: number): void {
    if (frameId <= this.retired) return;
    for (const id of this.reserved.keys()) if (id <= frameId) {
      if (!this.finished.has(id)) ++this.framesDropped;
      this.reserved.delete(id);
    }
    for (const id of this.finished) if (id <= frameId) this.finished.delete(id);
    this.retired = frameId; this.advance();
  }
  /** `confirmedLoss` is the subset of the missing report that later arrivals prove lost (not merely paced). */
  report(nowUs: number, snapshot: WcReceiveSnapshot, confirmedLoss: WcFeedback['missingFragments'] = snapshot.missingFragments): WcFeedback | null {
    // Reports stay frequent for a second after the last arrival: the host's flight window is only
    // released by acks, so a quiet stretch (a lost tail, a stalled host) must not slow them.
    const active = snapshot.reassemblyBytes + snapshot.decoderInFlight + snapshot.renderPending > 0 || nowUs - this.lastArrivalUs < ACTIVE_AFTER_ARRIVAL_US;
    const visibilityChanged = snapshot.visible !== this.lastVisible;
    // Newly observed loss (a fragment proven missing, or a packet id skipped) is reported at once (a NACK)
    // so the host's resend does not wait out the regular cadence.
    const missingKey = confirmedLoss.map(m => `${m.frameId}:${m.indices.join('.')}`).join(',');
    const newLoss = (missingKey !== '' && missingKey !== this.lastMissingKey) || this.gapsSeen !== this.gapsReported;
    // One NACK per regular interval: on a link that is dropping steadily, every arrival shows a new gap,
    // and a NACK for each would be hundreds of reports a second whose acknowledgements alone saturate a
    // narrow forward link, keeping it in the collapse the NACKs are meant to repair. The loss still goes
    // out on the next regular report; a repair a few milliseconds later is worth more than none at all.
    const nack = newLoss && (this.lastLossReportUs === null || nowUs - this.lastLossReportUs >= NACK_SPACING_US);
    // A report for every packet or two tells the host nothing a report every other interval would not,
    // and at a floor rate on a thin link each one costs that link an acknowledgement packet: a sparse
    // stream is reported at the slower cadence (still inside the host's freshness for receiver pressure).
    const regularUs = this.arrivalsSinceReport >= 2 ? 20000 : SPARSE_CADENCE_US;
    // The idle heartbeat is what brings a paused host back after a blackout (it resumes on the first
    // report through), so it is a tenth of a second, not a quarter: an idle report is a few dozen bytes.
    const cadenceUs = nack ? 2000 : visibilityChanged ? 5000 : active && snapshot.visible ? regularUs : 100000;
    if (this.lastReportUs !== null && nowUs - this.lastReportUs < cadenceUs) return null;
    if (this.sequence === WC_LIMITS.maxId) throw new Error('Feedback epoch exhausted');
    if (newLoss) this.lastLossReportUs = nowUs;
    this.arrivalsSinceReport = 0;
    this.lastReportUs = nowUs; this.lastVisible = snapshot.visible; this.lastMissingKey = missingKey; this.gapsReported = this.gapsSeen;
    const ranges: Array<[number, number]> = [];
    for (const id of [...this.packets].filter(id => id > 0 && id > this.highest - 8192).sort((a, b) => a - b)) {
      const last = ranges[ranges.length - 1]; if (last && id === last[1] + 1) last[1] = id; else ranges.push([id, id]);
    }
    const receivedRanges = ranges.slice(-64);
    // Each arrival is reported once. Repeating the last sixteen in every report told the host nothing
    // new about delay and made each report over a kilobyte, fifty times a second, on the return path.
    const arrivalSamples = this.arrivals.filter(sample => sample.arrivalUs <= nowUs && receivedRanges.some(([lo, hi]) => sample.packetId >= lo && sample.packetId <= hi));
    this.arrivals = this.arrivals.filter(sample => !arrivalSamples.includes(sample));
    const message: WcFeedback = { v: 1, type: 'feedback', session: this.session, epoch: this.epoch, sequence: ++this.sequence,
      clientNowUs: nowUs, highestPacketId: this.highest, receivedRanges, receivedBytes: this.receivedBytes, arrivalSamples,
      retiredThroughFrameId: this.retired, decodedFrameId: this.decoded, renderedFrameId: this.rendered,
      receiverWindowBytes: snapshot.visible ? WebCodecsFeedback.mediaWindowBytes : 0, ...snapshot,
      // A frame retired meanwhile (a recover between the snapshot and this report) is nobody's to repair.
      missingFragments: snapshot.missingFragments.filter(missing => missing.frameId > this.retired),
      decodeP95Us: this.percentile(this.decodeTimes) ?? 0, renderIntervalP95Us: this.percentile(this.renderTimes) ?? 0 };
    // The fixed bounds fit 8 KiB today; retain a byte check if DTO sizes ever change.
    WebCodecsWire.EncodeFeedback(message); return message;
  }
  stats(snapshot: WcReceiveSnapshot, reassembly: Pick<WcWorkerStats, 'repairHoldUs' | 'keyAsks'> = { repairHoldUs: 0, keyAsks: 0 }): WcWorkerStats {
    return { ...WcStats.emptyWorker(), epoch: this.epoch, receivedBytes: this.receivedBytes, framesDecoded: this.framesDecoded,
      framesPresented: this.framesPresented, framesDropped: this.framesDropped, reassemblyBytes: snapshot.reassemblyBytes,
      decoderInFlight: snapshot.decoderInFlight, renderPending: snapshot.renderPending, oldestPendingAgeUs: snapshot.oldestPendingAgeUs,
      decodeP95Us: this.percentile(this.decodeTimes), renderIntervalP95Us: this.percentile(this.renderTimes), ...reassembly };
  }
  /** A skipped packet id the host has not been told about yet: worth a report ahead of the timer. */
  hasUnreportedGap(): boolean { return this.gapsSeen !== this.gapsReported; }
  getOutstandingBytes(): number { return [...this.reserved.values()].reduce((sum, bytes) => sum + bytes, 0); }
  getRetiredThrough(): number { return this.retired; }
  getIngressDelayP95Us(): number | null { return this.percentile(this.ingressTimes); }
  private retire(frameId: number): void {
    if (frameId > this.retired && this.reserved.has(frameId)) { this.finished.add(frameId); this.advance(); }
  }
  private advance(): void {
    while (this.finished.delete(this.retired + 1)) { ++this.retired; this.reserved.delete(this.retired); }
  }
  private sample(samples: number[], value: number): void { samples.push(Math.ceil(value)); if (samples.length > 128) samples.shift(); }
  private percentile(samples: number[]): number | null {
    return samples.length ? [...samples].sort((a, b) => a - b)[Math.ceil(samples.length * 0.95) - 1] : null;
  }
}
